import { Injectable, Inject, RendererFactory2, DOCUMENT } from '@angular/core';

import type { Renderer2 } from '@angular/core';

/**
 * This Service is used for loading scripts into the header of the index page.
 * Should be used only for loading external scripts such as analytics.
 */

export type ScriptLoaderAttributes = Partial<HTMLScriptElement>;

@Injectable({
  providedIn: 'root'
})
export class ScriptLoaderService {

  private scripts: Set<string> = new Set<string>();
  private renderer: Renderer2;

  constructor(@Inject(DOCUMENT) private document: Document,
              @Inject(RendererFactory2) rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
  }

  load(...scripts: Array<string>): Promise<unknown> {
    const promises: Array<unknown> = [];
    scripts.forEach((script: string): number =>
      promises.push(this.loadScript({ src: script }))
    );

    return Promise.all(promises);
  }

  loadScripts(...attrsList: Array<ScriptLoaderAttributes>): Promise<unknown> {
    const promises: Array<unknown> = [];
    attrsList.forEach((attrs: ScriptLoaderAttributes): number =>
      promises.push(this.loadScript(attrs))
    );

    return Promise.all(promises);
  }

  loadScript(attrs: ScriptLoaderAttributes): Promise<unknown> {
    return new Promise((resolve: (object: Record<string, unknown>) => void, reject: (object: Record<string, unknown>) => void): void => {
      const script = attrs.src?.toString() ?? attrs.innerText?.toString();

      // resolve if already loaded
      if (this.scripts.has(script)) {
        resolve({ script, loaded: true, status: 'Already Loaded' });
      } else {
        // load script
        const element = this.renderer.createElement('script');
        element.type = 'text/javascript';
        this.loadAttributes(element, attrs);

        if (element.readyState) {  // IE
          element.onreadystatechange = (): void => {
            if (element.readyState === 'loaded' || element.readyState === 'complete') {
              element.onreadystatechange = null;
              this.scripts.add(script);
              resolve({ script, loaded: true, status: 'Loaded' });
            }
          };
        } else {  // Others
          element.onload = (): void => {
            this.scripts.add(script);
            resolve({ script, loaded: true, status: 'Loaded' });
          };
        }
        element.onerror = (error: unknown): void => reject({ script, loaded: false, status: error });

        this.renderer.appendChild(this.document.head, element);
      }
    });
  }

  removeScript(script: string): void {
    const element = this.document.head.querySelector(`script[src="${script}"]`);
    if (element) {
      this.renderer.removeChild(this.document.head, element);
      this.scripts.delete(script);
    }
  }

  private loadAttributes(target: HTMLElement | DOMStringMap, attrs: ScriptLoaderAttributes | DOMStringMap): void {
    if (!target || typeof attrs !== 'object') {
      return;
    }
    for (const name in attrs) {
      if (name === 'dataset') {
        this.loadAttributes(target.dataset as DOMStringMap, attrs.dataset as DOMStringMap);
      } else {
        target[name] = attrs[name];
      }
    }
  }
}
